import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { FaLock, FaEnvelope, FaSpinner } from 'react-icons/fa';
import PublicLayout from '../../components/PublicLayout';
import OTPVerification from '../../components/OTPVerification';
import '../../styles/Auth.css';
import googleLogo from '../../assets/google-logo.svg';

export default function TeamLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '' });
  const [showPlanRedirectModal, setShowPlanRedirectModal] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [otpError, setOtpError] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const toastTimeout = useRef(null);
  
  const { login, setUser } = useAuth();
  const navigate = useNavigate();

  const handleShowToast = (message) => {
    setToast({ open: true, message });
    if (toastTimeout.current) clearTimeout(toastTimeout.current);
    toastTimeout.current = setTimeout(() => {
      setToast({ open: false, message: '' });
    }, 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Call the API directly to get user data without automatic navigation
      // Use the same URL logic as axios configuration
      const API_URL = process.env.NODE_ENV === 'development' 
        ? '/api' 
        : (process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api');
      
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleShowToast('Invalid email or password');
        } else if (response.status === 503) {
          handleShowToast('Service temporarily unavailable. Please try again later.');
        } else {
          handleShowToast('An error occurred during login');
        }
        return;
      }

      const data = await response.json();
      
      // Check if 2FA is required
      if (data.requires2FA) {
        setRequires2FA(true);
        setSessionId(data.sessionId);
        return;
      }
      
      const userData = data.data.user;
      
      // Check if user has Free or Individual plan
      if (userData.currentPlanName === 'Free' || userData.currentPlanName === 'Individual') {
        setShowPlanRedirectModal(true);
        return;
      }
      
      // For Team plan, proceed with normal login
      const success = await login(email, password);
      if (success) {
        setShowLoadingScreen(true);
        setTimeout(() => {
          setShowLoadingScreen(false);
        }, 2000);
      }
    } catch (err) {
      // If there's an error with the direct API call, don't fall back to AuthContext login
      // because it will automatically navigate and bypass our plan detection
      console.error('Direct API call failed:', err);
      handleShowToast('Unable to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRedirectToRegularLogin = () => {
    setShowPlanRedirectModal(false);
    navigate('/login');
  };

  const handleCloseModal = () => {
    setShowPlanRedirectModal(false);
  };

  // Handle ESC key press
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && showPlanRedirectModal) {
        handleCloseModal();
      }
    };

    if (showPlanRedirectModal) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showPlanRedirectModal]);

  const getRegularLoginUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/login`;
  };

  const handleGoogleLogin = () => {
    // Google login tracking will be handled by GTM
  };

  const handleVerifyOTP = async (otpCode) => {
    setOtpLoading(true);
    setOtpError('');
    
    try {
      const API_URL = process.env.NODE_ENV === 'development' 
        ? '/api' 
        : (process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api');
      
      const response = await fetch(`${API_URL}/auth/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ otp: otpCode, sessionId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setOtpError(errorData.message || 'Invalid verification code. Please try again.');
        return;
      }

      const data = await response.json();
      const userData = data.data.user;
      
      // Check if user has Free or Individual plan
      if (userData.currentPlanName === 'Free' || userData.currentPlanName === 'Individual') {
        setRequires2FA(false);
        setShowPlanRedirectModal(true);
        return;
      }
      
      // OTP verified - session is already set, just update AuthContext and navigate
      setRequires2FA(false);
      
      // Set user in AuthContext and navigate directly
      setUser(userData);
      navigate('/app');
    } catch (err) {
      console.error('OTP verification failed:', err);
      setOtpError('Unable to verify code. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      const API_URL = process.env.NODE_ENV === 'development' 
        ? '/api' 
        : (process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api');
      
      const response = await fetch(`${API_URL}/auth/resend-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ sessionId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setOtpError(errorData.message || 'Failed to resend code. Please try again.');
        return;
      }

      const data = await response.json();
      handleShowToast(data.message || 'Verification code has been resent');
      setOtpError('');
    } catch (err) {
      console.error('Resend OTP failed:', err);
      setOtpError('Unable to resend code. Please try again.');
    }
  };

  // Show OTP verification if 2FA is required
  if (requires2FA && sessionId) {
    return (
      <PublicLayout>
        <div className="auth-container">
          <div className="auth-card">
            <OTPVerification
              sessionId={sessionId}
              email={email}
              onVerify={handleVerifyOTP}
              onResend={handleResendOTP}
              error={otpError}
              loading={otpLoading}
            />
          </div>
        </div>
      </PublicLayout>
    );
  }

  // Plan redirect modal
  if (showPlanRedirectModal) {
    return (
      <PublicLayout>
        <div className="modal-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={handleCloseModal}
        >
          <div className="modal-content" 
            style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '12px',
              maxWidth: '400px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginBottom: '1rem', color: '#333' }}>Non-Team Plan Detected</h2>
            <p style={{ marginBottom: '1rem', color: '#666', lineHeight: '1.5' }}>
              You have a Free or Individual plan. Please use the regular login page to access your account.
            </p>
            <p style={{ marginBottom: '2rem', color: '#888', fontSize: '14px', lineHeight: '1.4' }}>
              Regular login URL: <a href={getRegularLoginUrl()} style={{ color: '#3b9efc', textDecoration: 'none' }}>{getRegularLoginUrl()}</a>
            </p>
            <button
              onClick={handleRedirectToRegularLogin}
              style={{
                backgroundColor: '#3b9efc',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '16px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Go to Regular Login
            </button>
          </div>
        </div>
      </PublicLayout>
    );
  }

  // Loading screen overlay
  if (showLoadingScreen) {
    return (
      <PublicLayout>
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner">
              <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '48px', color: 'white' }} />
            </div>
            <h2>Signing you in...</h2>
            <p>Please wait while we authenticate your credentials</p>
            <div className="loading-steps">
              <div className="step active">
                <span className="step-number">1</span>
                <span className="step-text">Verifying credentials</span>
              </div>
              <div className="step">
                <span className="step-number">2</span>
                <span className="step-text">Loading your account</span>
              </div>
              <div className="step">
                <span className="step-number">3</span>
                <span className="step-text">Preparing your dashboard</span>
              </div>
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Team Log In</h1>
            <p><Link to="/login" style={{ color: '#3b9efc', textDecoration: 'none' }}>Click here for Free and Individual Plan Log In</Link></p>
          </div>
          
          {/* Social Login Buttons */}
          <div className="social-login-container">
            {/* Google Login Button */}
            <a
              href={process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api/auth/google` : '/api/auth/google'}
              className="google-login-btn"
              onClick={handleGoogleLogin}
            >
              <img src={googleLogo} alt="Google logo" className="google-logo" />
              Continue with Google
            </a>
          </div>

          <div className="divider">
            <span>or</span>
          </div>
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <div className="input-with-icon">
                <FaEnvelope className="input-icon" />
                <input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  aria-invalid={toast.open ? 'true' : 'false'}
                />
              </div>
            </div>
            
            <div className="form-group">
              <div className="label-with-link">
                <label htmlFor="password">Password</label>
                <Link to="/forgot-password" className="forgot-password">Forgot password?</Link>
              </div>
              <div className="input-with-icon">
                <FaLock className="input-icon" />
                <input
                  id="password"
                  type="password"
                  placeholder="Your password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  aria-invalid={toast.open ? 'true' : 'false'}
                />
              </div>
            </div>
            
            <button 
              type="submit"
              className="auth-button"
              disabled={loading}
              aria-busy={loading}
            >
              {loading ? (
                <>
                  <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '16px' }} />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
          
          <div className="auth-footer">
            <p>Don't have an account? <Link to="/team-signup">Sign Up</Link></p>
          </div>
          {/* Error Toast - now just below the footer */}
          {toast.open && (
            <div className="login-toast-error" style={{  border: '1px solid #e53935', boxShadow: '0 4px 24px rgba(0,0,0,0.10)', alignItems: 'center', borderRadius: '8px', position: 'relative' }}>
              <button 
                onClick={() => setToast({ open: false, message: '' })}
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  background: 'none',
                  border: 'none',
                  fontSize: '18px',
                  cursor: 'pointer',
                  color: '#e53935',
                  padding: '0',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Close"
              >
                ×
              </button>
              <div className="login-toast-content">
                <div className="login-toast-title" style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '2px', color: '#222', paddingLeft: '10px'}}>❗ Error</div>
                <div className="login-toast-message" style={{ fontSize: '0.75rem', color: '#e53935', paddingLeft: '10px', paddingRight: '24px'}}>{toast.message}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </PublicLayout>
  );
}
